export const PHASE_ONE_STATES = ["MN", "WI", "IA", "IL", "MI"];

export const MONTH_OPTIONS = [
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
];

export const CLARITY_CLASSES = [
  {
    id: "excellent",
    label: "Excellent",
    threshold: ">= 4.0 m Secchi median",
    color: "#0f766e",
    note: "Strong historical water clarity for planning.",
  },
  {
    id: "good",
    label: "Good",
    threshold: "2.5 to < 4.0 m",
    color: "#65a30d",
    note: "Usually workable, but seasonal checks still matter.",
  },
  {
    id: "fair",
    label: "Fair",
    threshold: "1.2 to < 2.5 m",
    color: "#d97706",
    note: "Conditions may support acquisition only in favorable windows.",
  },
  {
    id: "poor",
    label: "Poor",
    threshold: "< 1.2 m",
    color: "#b91c1c",
    note: "Higher planning risk for bathy lidar penetration.",
  },
];

export const NORMALIZED_FIELDS = [
  { name: "lake_id", description: "Stable polygon identifier used across ingestion outputs." },
  { name: "lake_name", description: "Human-readable lake name from the boundary source." },
  { name: "state", description: "Two-letter state code." },
  { name: "month", description: "Two-digit month used for monthly climatology summaries." },
  { name: "secchi_median_m", description: "Median historical Secchi depth in meters." },
  { name: "secchi_p25_m", description: "Lower quartile historical Secchi depth in meters." },
  { name: "secchi_p75_m", description: "Upper quartile historical Secchi depth in meters." },
  { name: "sample_count", description: "Number of valid measurements contributing to the monthly estimate." },
  { name: "year_span_start", description: "Earliest year represented in the monthly summary." },
  { name: "year_span_end", description: "Latest year represented in the monthly summary." },
  { name: "confidence_score", description: "0-100 score derived from sample count, spread, and recency." },
  { name: "confidence_class", description: "High, Medium, or Low user-facing confidence label." },
  { name: "source_mix", description: "Delimited list of contributing programs or source families." },
  { name: "suitability_class", description: "Planning heuristic driven by clarity and confidence." },
];

export const PUBLIC_SOURCES = [
  {
    state: "Regional",
    name: "Water Quality Portal",
    kind: "Cross-state observations",
    priority: "Core base layer",
    url: "https://www.waterqualitydata.us/",
    notes: "Best first stop for historical Secchi and related field measurements across agencies.",
  },
  {
    state: "MN",
    name: "Minnesota LakeFinder",
    kind: "Lake summary and field observations",
    priority: "High",
    url: "https://www.dnr.state.mn.us/Lakefinder",
    notes: "Useful for lake metadata and water quality context.",
  },
  {
    state: "MN",
    name: "Minnesota LakeBrowser",
    kind: "Satellite-derived clarity",
    priority: "High",
    url: "https://lakes.rs.umn.edu/",
    notes: "Strong backfill source when field observations are sparse.",
  },
  {
    state: "WI",
    name: "Wisconsin DNR SWIMS",
    kind: "Monitoring records",
    priority: "High",
    url: "https://dnr.wisconsin.gov/topic/surfacewater/swims/",
    notes: "Volunteer and agency monitoring with strong lake coverage.",
  },
  {
    state: "WI",
    name: "Wisconsin Satellite Monitoring",
    kind: "Satellite-derived Secchi",
    priority: "High",
    url: "https://dnr.wisconsin.gov/topic/lakes/satellitemonitoring.html",
    notes: "Important statewide complement to in-situ records.",
  },
  {
    state: "IA",
    name: "Iowa AQuIA",
    kind: "Monitoring records",
    priority: "High",
    url: "https://programs.iowadnr.gov/aquia/",
    notes: "Good phase-one source for inland-lake observations.",
  },
  {
    state: "IL",
    name: "Illinois VLMP Data",
    kind: "Volunteer monitoring",
    priority: "Phase-two candidate",
    url: "https://epa.illinois.gov/topics/water-quality/monitoring/vlmp/data.html",
    notes: "Likely useful, but integration may be more fragmented.",
  },
  {
    state: "MI",
    name: "Michigan Inland Lakes Monitoring",
    kind: "State monitoring program",
    priority: "Phase-two candidate",
    url: "https://www.michigan.gov/egle/about/organization/water-resources/inland-lakes-monitoring",
    notes: "Worth adding once the phase-one pipeline is stable.",
  },
];

export const BUILD_STEPS = [
  "Build an ETL script that harvests Secchi observations and lake boundaries into a raw staging folder.",
  "Resolve observations to stable lake polygons and produce monthly summaries with explicit provenance.",
  "Publish a compact app-ready polygon layer, ideally GeoJSON first and PMTiles later if size grows.",
  "Render the layer in the Water Clarity tool with month filters, confidence classes, and click-through details.",
  "Add a lidar suitability heuristic only after the raw clarity and confidence layers feel trustworthy.",
];
